import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { CalendarDays, ListChecks, RefreshCw, Users } from "lucide-react";
import {
    Chart as ChartJS, BarElement, CategoryScale, Legend, LinearScale, Tooltip,
} from "chart.js";
import { ContainerCard } from "../../../components/ui/cards";
import { Button } from "../../../components/ui/button";
import ToastMsg from "../../../components/common/ToastMsg";
import { AuthContext } from "../../../context/AuthContext";
import useApiMethods from "../../../lib/useApiMethods";
import { AI_SUGGESTION_UPDATE, DASHBOARD_MY } from "../../../constants/api";
import { cn } from "../../../utils/tailwindUtils";
import TodayStrip from "./TodayStrip";
import DoNextQueue from "./DoNextQueue";
import TargetRings from "./TargetRings";
import UpcomingDemos from "./UpcomingDemos";
import MyPipeline from "./MyPipeline";
import WeeklyActivity from "./WeeklyActivity";
import MeVsTeam from "./MeVsTeam";
import CoachTip from "./CoachTip";
import RecentActivity from "./RecentActivity";

dayjs.extend(relativeTime);
ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const REFRESH_MS = 5 * 60 * 1000;

const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    return "Good evening";
};

// "My Day": the dashboard for a sales rep (role_type 'user'). Everything comes
// from one call to /dashboard/my/, scoped to the leads assigned to them.
const RepDashboard = () => {
    const { fetch, update } = useApiMethods();
    const navigate = useNavigate();
    const { hasPermission, hasFeature } = useContext(AuthContext);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const lastLoad = useRef(0);

    const load = useCallback((quiet = false) => {
        quiet ? setRefreshing(true) : setLoading(true);
        lastLoad.current = Date.now();
        fetch(DASHBOARD_MY)
            .then(res => {
                if (res?.status === "success") setData(res.data);
                else if (!quiet) ToastMsg("error", res?.message || "Failed to load your dashboard");
            })
            .catch(() => { if (!quiet) ToastMsg("error", "Failed to load your dashboard"); })
            .finally(() => { setLoading(false); setRefreshing(false); });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Load once, then keep it fresh: every few minutes, and when the rep comes
    // back to the tab after being away (unless it was only a moment ago).
    useEffect(() => {
        load();
        const timer = setInterval(() => load(true), REFRESH_MS);
        const onFocus = () => { if (Date.now() - lastLoad.current > 60 * 1000) load(true); };
        window.addEventListener("focus", onFocus);
        return () => { clearInterval(timer); window.removeEventListener("focus", onFocus); };
    }, [load]);

    const openLead = (customerId) => navigate("/customers/customerdetails", {
        state: { formEdit: true, row: { id: customerId } },
    });

    const canFollowup = hasPermission("followup", "view");
    const go = (where) => {
        if (where === "followup" && canFollowup) navigate("/followup");
        else if (where === "demos" && canFollowup) navigate("/demos/calendar");
        else navigate("/customers");
    };

    // Done / dismissed leave the queue at once; the refresh that follows puts
    // the lead back if it is still due for another reason. Put the row back
    // if the update fails.
    const onSuggestion = (item, status) => {
        const before = data;
        setData(d => ({ ...d, do_next: d.do_next.filter(r => r.customer_id !== item.customer_id) }));
        update(AI_SUGGESTION_UPDATE(item.suggestion_id), { status })
            .then(res => {
                if (res?.status !== "success") throw new Error(res?.message);
                ToastMsg("success", status === "done" ? "Marked done" : "Suggestion dismissed");
                load(true);
            })
            .catch(() => { setData(before); ToastMsg("error", "Could not update the suggestion"); });
    };

    const t = data?.today;
    const urgent = (t?.overdue || 0) + (t?.followups_due_today || 0);

    return (
        <ContainerCard>
            {/* ── hero ── */}
            <div className="rounded-2xl p-5 mb-4 text-white shadow-sm"
                 style={{ background: "linear-gradient(120deg, var(--brand-from, #9c5def), var(--brand-to, #bd56ea))" }}>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="min-w-0">
                        <p className="text-xs/5 opacity-80">{dayjs().format("dddd, D MMMM")}</p>
                        <h1 className="text-xl md:text-2xl font-semibold truncate">
                            {greeting()}, {data?.user?.first_name || "there"}
                        </h1>
                        <p className="text-sm opacity-90 mt-0.5">
                            {loading ? "Getting your day ready…"
                                : urgent ? `${urgent} follow-up${urgent === 1 ? "" : "s"} need you today${t?.overdue ? `, ${t.overdue} overdue` : ""}.`
                                : "You're all caught up. Nice work."}
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {hasFeature("customers") && (
                            <Button size="sm" onClick={() => navigate("/customers")}
                                    className="bg-white/15 hover:bg-white/25 text-white border border-white/30">
                                <Users className="h-3.5 w-3.5 mr-1" /> My leads
                            </Button>
                        )}
                        {canFollowup && (
                            <>
                                <Button size="sm" onClick={() => navigate("/followup")}
                                        className="bg-white/15 hover:bg-white/25 text-white border border-white/30">
                                    <ListChecks className="h-3.5 w-3.5 mr-1" /> Follow-ups
                                </Button>
                                <Button size="sm" onClick={() => navigate("/demos/calendar")}
                                        className="bg-white/15 hover:bg-white/25 text-white border border-white/30">
                                    <CalendarDays className="h-3.5 w-3.5 mr-1" /> Calendar
                                </Button>
                            </>
                        )}
                        <Button size="sm" onClick={() => load(true)} disabled={loading || refreshing}
                                aria-label="Refresh"
                                className="bg-white text-brand-700 hover:bg-white/90">
                            <RefreshCw className={cn("h-3.5 w-3.5", refreshing && "animate-spin")} />
                        </Button>
                    </div>
                </div>
            </div>

            <TodayStrip today={t} loading={loading} go={go} />

            {t?.unassigned_pool > 0 && (
                <button type="button" onClick={() => navigate("/customers")}
                        className="mt-3 w-full text-left text-xs rounded-xl px-3 py-2 bg-amber-50 text-amber-800 border border-amber-200 dark:bg-amber-900/20 dark:text-amber-200 dark:border-amber-800">
                    {t.unassigned_pool} unassigned lead{t.unassigned_pool === 1 ? " is" : "s are"} waiting in the pool. Pick one up →
                </button>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 mt-4">
                <div className="lg:col-span-8 space-y-4 min-w-0">
                    <DoNextQueue
                        items={data?.do_next}
                        total={data?.do_next_total || 0}
                        counts={data?.do_next_counts}
                        loading={loading}
                        onOpen={openLead}
                        onSuggestion={onSuggestion}
                        onViewAll={() => go("followup")}
                        uncontacted={t?.uncontacted_new}
                    />
                    <WeeklyActivity activity={data?.activity} loading={loading} />
                </div>
                <div className="lg:col-span-4 space-y-4 min-w-0">
                    <TargetRings targets={data?.targets} loading={loading} />
                    <UpcomingDemos demos={data?.demos} loading={loading} onOpen={openLead}
                                   onViewAll={() => go("demos")} />
                    <CoachTip coaching={data?.coaching} loading={loading} />
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4 mb-2">
                <MyPipeline pipeline={data?.pipeline} stuck={data?.stuck} loading={loading}
                            onOpen={openLead} onViewAll={() => go("leads")} />
                <MeVsTeam data={data?.me_vs_team} loading={loading} />
                <RecentActivity items={data?.recent} loading={loading} onOpen={openLead} />
            </div>

            {data?.generated_at && (
                <p className="text-[11px] text-gray-400 text-center pb-2">
                    Updated {dayjs(data.generated_at).format("h:mm A")} · refreshes every 5 minutes
                </p>
            )}
        </ContainerCard>
    );
};

export default RepDashboard;
